import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import _get from 'lodash/get';

import { AuthContext } from './AuthContext';
import useStore from 'utils/hooks/useStore';
import useUserMetaData from 'utils/hooks/useUserMetaData';
import isInternalUser from 'utils/methods/isInternalUser';

const analytics = new AnalyticsBrowser();
const SS_API_KEY = process.env.REACT_APP_SS_SEGMENT_API_KEY;
const RR_API_KEY = process.env.REACT_APP_RR_SEGMENT_API_KEY;

/**
 * @type {import('react').Context<{
 *   track: (eventName: string, eventProperties?: Record<string, any>) => void;
 *   page: (pageName?: string, pageProperties?: Record<string, any>) => void;
 * }>}
 */
export const SegmentContext = createContext();

export function SegmentProvider({ children = null }) {
  const { userData } = useContext(AuthContext);
  const { getStoreProperty } = useStore();
  const userMetaData = useUserMetaData();

  const userId = _get(userData, 'userId');
  const email = _get(userData, 'email');
  const isSSR = getStoreProperty('is_ssr_retailer', false);

  const apiKey = isSSR ? SS_API_KEY : RR_API_KEY;
  const isTrackable = apiKey && userId && !isInternalUser(email);

  useEffect(() => {
    if (!isTrackable) return;

    try {
      analytics.load({ writeKey: apiKey });
      analytics.identify(userId, userMetaData);
    } catch (error) {
      console.error('Failed to identify user in Segment:', error);
    }

    return () => {
      try {
        analytics.reset();
      } catch (error) {
        console.error('Failed to reset Segment:', error);
      }
    };
  }, [isTrackable, apiKey, userId, userMetaData]);

  const track = useCallback(
    (eventName, eventProperties = {}) => {
      if (!isTrackable || !eventName) return;

      try {
        analytics.track(eventName, { ...userMetaData, ...eventProperties });
      } catch (error) {
        console.error('Failed to track event in Segment:', error);
      }
    },
    [isTrackable, userMetaData],
  );

  const page = useCallback(
    (pageName = '', pageProperties = {}) => {
      if (!isTrackable || !pageName) return;

      try {
        analytics.page(pageName, { ...userMetaData, ...pageProperties });
      } catch (error) {
        console.error('Failed to track page view in Segment:', error);
      }
    },
    [isTrackable, userMetaData],
  );

  return (
    <SegmentContext.Provider value={{ track, page }}>
      {children}
    </SegmentContext.Provider>
  );
}
